import type {
  PermissionBuilder,
  PermissionCondition,
  PermissionRule,
} from "../types";

/**
 * Builder fluente para criar permissões
 */
export class PermissionRuleBuilder<TContext = any>
  implements PermissionBuilder<TContext>
{
  private rule: Partial<PermissionRule<TContext>> = {};

  action(action: string): this {
    this.rule.action = action;
    return this;
  }

  resource(resource: string): this {
    this.rule.resource = resource;
    return this;
  }

  when(condition: PermissionCondition<TContext>): this {
    if (!this.rule.conditions) {
      this.rule.conditions = [];
    }
    this.rule.conditions.push(condition);
    return this;
  }

  deny(): this {
    this.rule.deny = true;
    return this;
  }

  priority(value: number): this {
    this.rule.priority = value;
    return this;
  }

  build(): PermissionRule<TContext> {
    if (!this.rule.action) {
      throw new Error("Permission must have an action");
    }

    return this.rule as PermissionRule<TContext>;
  }
}

/**
 * Factory para criar builder de permissão
 */
export function permission<TContext = any>(): PermissionBuilder<TContext> {
  return new PermissionRuleBuilder<TContext>();
}

/**
 * Helpers para criar permissões comuns rapidamente
 */
export const permissions = {
  // CRUD básico
  crud: (resource: string) => [
    { action: "create", resource },
    { action: "read", resource },
    { action: "update", resource },
    { action: "delete", resource },
  ],

  // Todas as ações em um recurso
  all: (resource: string) => ({
    action: "*",
    resource,
  }),

  // Ações de leitura
  readonly: (resource: string) => [
    { action: "read", resource },
    { action: "list", resource },
    { action: "view", resource },
  ],

  // Ações de escrita
  write: (resource: string) => [
    { action: "create", resource },
    { action: "update", resource },
    { action: "delete", resource },
  ],

  // Gerenciamento completo
  manage: (resource: string) => ({
    action: "manage",
    resource,
  }),
};

/**
 * Helper para criar condições comuns
 */
export const conditions = {
  // Verifica se é proprietário
  isOwner: <T extends { userId?: any; ownerId?: any }>(
    userIdField = "userId",
  ): PermissionCondition<{ user: { id: any }; resource: T }> => {
    return (context) => {
      if (!context.user || !context.user.id) return false;
      if (!context.resource) return false;

      const userId = context.user.id;
      const resourceOwnerId =
        (context.resource as any)[userIdField] || context.resource.ownerId;

      if (!resourceOwnerId) return false;

      return userId === resourceOwnerId;
    };
  },

  // Verifica se recurso está em determinado status
  hasStatus: <T extends { status?: string }>(
    ...statuses: string[]
  ): PermissionCondition<{ resource: T }> => {
    return (context) => {
      const resourceStatus = context.resource?.status;
      return resourceStatus ? statuses.includes(resourceStatus) : false;
    };
  },

  // Verifica se usuário tem atributo específico
  userHasAttribute: (
    attribute: string,
    value?: any,
  ): PermissionCondition<{ user: { attributes?: Record<string, any> } }> => {
    return (context) => {
      const userAttribute = context.user?.attributes?.[attribute];
      return value !== undefined
        ? userAttribute === value
        : userAttribute !== undefined;
    };
  },

  // Verifica se recurso foi criado há menos de X tempo
  createdWithin: <T extends { createdAt?: Date | string }>(
    milliseconds: number,
  ): PermissionCondition<{ resource: T }> => {
    return (context) => {
      const createdAt = context.resource?.createdAt;
      if (!createdAt) return false;

      const createdTime =
        createdAt instanceof Date
          ? createdAt.getTime()
          : new Date(createdAt).getTime();
      return Date.now() - createdTime <= milliseconds;
    };
  },

  // Combina múltiplas condições com AND
  and: <TContext>(
    ...conditions: PermissionCondition<TContext>[]
  ): PermissionCondition<TContext> => {
    return (context) => conditions.every((condition) => condition(context));
  },

  // Combina múltiplas condições com OR
  or: <TContext>(
    ...conditions: PermissionCondition<TContext>[]
  ): PermissionCondition<TContext> => {
    return (context) => conditions.some((condition) => condition(context));
  },

  // Nega uma condição
  not: <TContext>(
    condition: PermissionCondition<TContext>,
  ): PermissionCondition<TContext> => {
    return (context) => !condition(context);
  },
};
